'use client'

import React, { useEffect, useContext } from 'react'
import { useSearch } from '@/src/contexts'
import { AmplitudeContext } from '@/src/contexts/Amplitude/AmplitudeContext'
import { trackGeolocationEvents } from '@/src/lib'

import { Typography } from 'antd'
import { Button } from '@/src/components'

import { CurrentAddressData } from '@/src/interfaces'
import {
  DEFAULT_DISTANCE,
  DEFAULT_US_ADDRESS,
  DEFAULT_US_COORDINATES,
} from '@/src/data'

import '@fortawesome/fontawesome-svg-core/styles.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import '@/src/lib'

import { Coordinates, GeoLocation, QueryStringParams } from '@/src/interfaces'

const { Text } = Typography

interface GeoLocationButtonProps {
  geoLocation: GeoLocation | null
  addressData: CurrentAddressData | null
  loadingGeoLocation: boolean
  geoLocationError: string | null
  fetchLocation: () => Promise<void>
  getCoordinatesFromAddress: (address: string) => Promise<Coordinates | null>
  nonUSMessage: string
  setNonUSMessage: (message: string) => void
  shouldShowAddress: boolean
  geoLocationAddress: string
  searchParams: QueryStringParams
  setSearchParams: (params: QueryStringParams) => void
  isUS: boolean
}

export const GeoLocationButton: React.FC<GeoLocationButtonProps> = ({
  geoLocation,
  addressData,
  loadingGeoLocation,
  geoLocationError,
  fetchLocation,
  nonUSMessage,
  setNonUSMessage,
  shouldShowAddress,
  geoLocationAddress,
  searchParams,
  setSearchParams,
  isUS,
}) => {
  const { setQuery, setLocationError } = useSearch()
  const { trackAmplitudeEvent } = useContext(AmplitudeContext)

  useEffect(() => {
    if (addressData) {
      if (isUS) {
        setQuery((prevQuery) => ({
          ...prevQuery,
          locationParameters: {
            state: addressData.state,
            city: addressData.city,
            zip: addressData.zip,
          },
          geoDistanceParameters: {
            distance:
              prevQuery.geoDistanceParameters?.distance || DEFAULT_DISTANCE,
            currentLocation: {
              lat: geoLocation?.latitude || 0,
              lon: geoLocation?.longitude || 0,
            },
          },
        }))
      } else {
        setQuery((prevQuery) => ({
          ...prevQuery,
          locationParameters: DEFAULT_US_ADDRESS,
          geoDistanceParameters: {
            distance: DEFAULT_DISTANCE,
            currentLocation: DEFAULT_US_COORDINATES,
          },
        }))
      }
    }
  }, [addressData, setQuery])

  useEffect(() => {
    if (shouldShowAddress || !addressData?.fullAddress) {
      setNonUSMessage('')
    }
  }, [shouldShowAddress, addressData])

  const handleFetchLocation = () => {
    setLocationError(null)
    fetchLocation()
    setSearchParams({
      ...searchParams,
      sortOrder: 'asc',
      sortBy: 'distance',
    })

    // Amplitude: Track the geolocation button click
    trackGeolocationEvents.clicked(trackAmplitudeEvent)({
      'Current Location Exists': !!geoLocation,
    })
  }

  return (
    <>
      {geoLocationError && (
        <div className='text-red-500'>{geoLocationError}</div>
      )}
      {shouldShowAddress && !loadingGeoLocation && (
        <>
          <div className='p-2 border rounded-md bg-gray-50'>
            <div className='flex justify-between items-start'>
              <div>
                <Text className='block'>
                  <FontAwesomeIcon
                    icon='location-dot'
                    className='text-primary'
                  />{' '}
                  Current location:
                </Text>
                <Text strong className='block'>
                  {geoLocationAddress}
                </Text>
              </div>
            </div>
          </div>
          <Text type='danger'>{nonUSMessage || ''}</Text>
        </>
      )}

      {!shouldShowAddress && (
        <Button
          type='primary'
          onClick={handleFetchLocation}
          loading={loadingGeoLocation}
          block
          aria-label='Get your current location'
          disabled={loadingGeoLocation}
          className='w-full'
          aria-busy={loadingGeoLocation}
          text={
            loadingGeoLocation
              ? 'Getting location...'
              : 'Use my current location'
          }
        />
      )}
    </>
  )
}
